import { forwardRef } from 'react';
import { Opportunity, OPPORTUNITY_STATUSES } from '@/hooks/useOpportunities';

interface OpportunitiesPipelineExportProps {
  opportunities: Opportunity[];
}

const BASE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const OpportunitiesPipelineExport = forwardRef<HTMLDivElement, OpportunitiesPipelineExportProps>(
  ({ opportunities }, ref) => {
    const totalFleet = opportunities.reduce((sum, opp) => sum + (opp.total_fleet || 0), 0);
    const totalElectricTrucks = opportunities.reduce((sum, opp) => sum + (opp.electric_trucks || 0), 0);

    return (
      <div 
        ref={ref}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '900px',
          padding: '24px',
          backgroundColor: 'white',
          fontFamily: BASE_FONT,
          fontStyle: 'normal',
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <div>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 700, 
              color: '#111827', 
              margin: 0,
              fontFamily: BASE_FONT,
              fontStyle: 'normal',
            }}>
              Pipeline Overzicht
            </h1>
            <p style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              margin: '4px 0 0 0',
              fontFamily: BASE_FONT,
              fontStyle: 'normal',
            }}>
              {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          
          {/* Fleet totals */}
          <div style={{ display: 'flex', gap: '24px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '6px', 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#111827',
                fontFamily: BASE_FONT,
                fontStyle: 'normal',
              }}>
                {/* Truck SVG inline */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
                  <path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.948V8.5A1.5 1.5 0 0 1 17.209 7H19a2 2 0 0 1 2 2v8a1 1 0 0 1-1 1h-2"/>
                  <circle cx="7" cy="18" r="2"/>
                  <circle cx="17" cy="18" r="2"/>
                </svg>
                {totalFleet.toLocaleString('nl-NL')}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                fontFamily: BASE_FONT,
                fontStyle: 'normal',
              }}>Totale vloot</div>
            </div>
            <div style={{ width: '1px', backgroundColor: '#e5e7eb' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '6px', 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#16a34a',
                fontFamily: BASE_FONT,
                fontStyle: 'normal',
              }}>
                {/* Zap SVG inline */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
                {totalElectricTrucks.toLocaleString('nl-NL')}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                fontFamily: BASE_FONT,
                fontStyle: 'normal',
              }}>E-trucks</div>
            </div>
          </div>
        </div>

        {/* Status breakdown */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(6, 1fr)', 
          gap: '16px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
        }}>
          {OPPORTUNITY_STATUSES.map(status => {
            const count = opportunities.filter(o => o.status === status.id).length;
            const fleetInStatus = opportunities
              .filter(o => o.status === status.id)
              .reduce((sum, o) => sum + (o.total_fleet || 0), 0);
            
            return (
              <div key={status.id} style={{ textAlign: 'center' }}>
                <div 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    borderRadius: '50%', 
                    backgroundColor: status.color,
                    margin: '0 auto 8px',
                  }} 
                />
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 700, 
                  color: '#111827',
                  fontFamily: BASE_FONT,
                  fontStyle: 'normal',
                }}>
                  {count}
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  marginBottom: '4px',
                  fontFamily: BASE_FONT,
                  fontStyle: 'normal',
                }}>
                  {status.label}
                </div>
                {fleetInStatus > 0 && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    fontFamily: BASE_FONT,
                    fontStyle: 'normal',
                  }}>
                    {/* Small truck SVG */}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
                      <path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.948V8.5A1.5 1.5 0 0 1 17.209 7H19a2 2 0 0 1 2 2v8a1 1 0 0 1-1 1h-2"/>
                      <circle cx="7" cy="18" r="2"/>
                      <circle cx="17" cy="18" r="2"/>
                    </svg>
                    {fleetInStatus.toLocaleString('nl-NL')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ 
          marginTop: '16px', 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#9ca3af',
          fontSize: '11px',
          fontFamily: BASE_FONT,
          fontStyle: 'normal',
        }}>
          <span>Greenvision Energy</span>
          <span>Totaal: {opportunities.length} opportunities</span>
        </div>
      </div>
    );
  }
);

OpportunitiesPipelineExport.displayName = 'OpportunitiesPipelineExport';
